import { useCallback, useState } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import useOrganizations from '@/features/organizations/useOrganizations';
import { useAppSelector } from '@/hooks';
import { useFetchBranchQuery } from '@/features/organizations/api/branchApi';
import { skipToken } from '@reduxjs/toolkit/dist/query';

const LIBRARIES: ("places")[] = ["places"];

const useGetDistance = () => {
    const { currentOrganization } = useOrganizations();
    const organizationId = useAppSelector((state) => state.ui.activeOrganization);
    const activeBranchId = useAppSelector((state) => state.ui.activeBranch);
    const [isLoading, setIsLoading] = useState(false);
    const [distance, setDistance] = useState<{ text: string; number: number } | null>(null);
    const [duration, setDuration] = useState<{ text: string; number: number } | null>(null);
    const [status, setStatus] = useState<string | null>(null);

    const { data: branches = [] } = useFetchBranchQuery(organizationId ?? skipToken, {
        skip: !organizationId,
    });

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: import.meta.env.VITE_FIREBASE_API_KEY,
        libraries: LIBRARIES
    });

    const calculate = useCallback(async (destLat?: number, destLng?: number) => {
        const activeBranch = activeBranchId ? branches.find((b) => b.id === activeBranchId) : null;
        const originLat = activeBranch?.location?.latitude ?? currentOrganization?.latitude;
        const originLng = activeBranch?.location?.longitude ?? currentOrganization?.longitude;

        if (!window.google || !isLoaded || !originLat || !originLng || destLat === undefined || destLng === undefined) {
            return null;
        }

        let newDistance = null;
        let newDuration = null;
        const service = new window.google.maps.DistanceMatrixService();

        setIsLoading(true);

        await service.getDistanceMatrix(
            {
                origins: [{ lat: originLat, lng: originLng }],
                destinations: [{ lat: destLat, lng: destLng }],
                travelMode: window.google.maps.TravelMode.DRIVING,
                unitSystem: window.google.maps.UnitSystem.METRIC,
            },
            (response, status) => {
                setStatus(status);
                if (status === "OK" && response?.rows[0].elements[0].status === "OK") {
                    const element = response.rows[0].elements[0];
                    newDistance = { text: element.distance.text, value: element.distance.value };
                    newDuration = { text: element.duration.text, value: element.duration.value };
                }
                setIsLoading(false);
            }
        );

        setDistance(newDistance);
        setDuration(newDuration);

        return { distance: newDistance, duration: newDuration };
    }, [isLoaded, activeBranchId, branches, currentOrganization?.latitude, currentOrganization?.longitude]);

    return { calculate, distance, duration, isLoading, status };
}

export default useGetDistance;
